import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { extractErrorMessage } from '../utils/errors';
import { useToast } from './Toast';
import { useAuth } from '../auth/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ── Timezone helpers ──────────────────────────────────────────────────────────

const TIMEZONES = [
  { label: 'UTC',                         value: 'UTC' },
  { label: 'Europe/Warsaw  (CET/CEST)',    value: 'Europe/Warsaw' },
  { label: 'Europe/London  (GMT/BST)',     value: 'Europe/London' },
  { label: 'Europe/Paris   (CET/CEST)',    value: 'Europe/Paris' },
  { label: 'Europe/Berlin  (CET/CEST)',    value: 'Europe/Berlin' },
  { label: 'Europe/Helsinki (EET/EEST)',   value: 'Europe/Helsinki' },
  { label: 'Europe/Moscow  (MSK)',         value: 'Europe/Moscow' },
  { label: 'US/Eastern     (EST/EDT)',     value: 'America/New_York' },
  { label: 'US/Central     (CST/CDT)',     value: 'America/Chicago' },
  { label: 'US/Mountain    (MST/MDT)',     value: 'America/Denver' },
  { label: 'US/Pacific     (PST/PDT)',     value: 'America/Los_Angeles' },
  { label: 'Asia/Dubai     (GST)',         value: 'Asia/Dubai' },
  { label: 'Asia/Singapore (SGT)',         value: 'Asia/Singapore' },
  { label: 'Asia/Tokyo     (JST)',         value: 'Asia/Tokyo' },
  { label: 'Australia/Sydney (AEST/AEDT)',value: 'Australia/Sydney' },
];

/** Format a UTC ISO string for display in the given IANA timezone. */
function fmtDate(iso?: string, tz = 'UTC'): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    timeZone: tz,
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** Get the UTC offset (in minutes) for an IANA timezone at a given instant. */
function getTzOffsetMinutes(date: Date, iana: string): number {
  const toMs = (d: Date, zone: string) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: false,
    }).formatToParts(d);
    const get = (t: string) => parseInt(parts.find(p => p.type === t)?.value ?? '0');
    // hour12:false gives 24 for midnight — normalise it
    const h = get('hour') % 24;
    return Date.UTC(get('year'), get('month') - 1, get('day'), h, get('minute'), get('second'));
  };
  return (toMs(date, iana) - toMs(date, 'UTC')) / 60000;
}

/**
 * Convert a naive datetime-local string (e.g. "2024-01-15T22:00") that the
 * user intends as wall-clock time in `iana` timezone into a UTC ISO string.
 */
function naiveDtToUtcIso(naiveDt: string, iana: string): string {
  if (!naiveDt) return '';
  const approx = new Date(naiveDt + ':00Z'); // treat as UTC first
  const offsetMin = getTzOffsetMinutes(approx, iana);
  return new Date(approx.getTime() - offsetMin * 60000).toISOString();
}

/** Today's date (YYYY-MM-DD) as seen in the given IANA timezone. */
function currentDateIn(iana: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: iana, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const get = (t: string) => parts.find(x => x.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ScheduledJob {
  id: string;
  name: string;
  description: string;
  vmIds: string[];
  vmNames: string[];
  action: string;
  scheduleType: 'Once' | 'Interval' | 'Weekly';
  runAt?: string;
  intervalMinutes?: number;
  intervalStartAt?: string;
  intervalEndAt?: string;
  weeklyDays?: number[];
  weeklyTimeUtc?: string;
  status: 'Active' | 'Disabled' | 'Completed' | 'Failed';
  createdAt: string;
  lastRunAt?: string;
  nextRunAt?: string;
  runCount: number;
  recentLogs: JobExecutionLog[];
}

interface JobExecutionLog {
  executedAt: string;
  success: boolean;
  message: string;
  affectedVms: number;
  errors: string[];
}

interface VmOption {
  id: string;
  name: string;
  powerState: string;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ACTIONS = ['Start', 'Stop', 'Restart', 'Deallocate'];

const ACTION_COLOR: Record<string, string> = {
  Start: '#28a745',
  Stop: '#ffc107',
  Restart: '#17a2b8',
  Deallocate: '#dc3545',
};

const STATUS_COLOR: Record<string, string> = {
  Active: '#28a745',
  Disabled: '#6c757d',
  Completed: '#17a2b8',
  Failed: '#dc3545',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtRelative(iso?: string): string {
  if (!iso) return '—';
  const diff = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  const hrs = Math.round(abs / 3600000);
  const days = Math.round(abs / 86400000);
  const suffix = diff < 0 ? 'ago' : 'from now';
  if (mins < 2) return `just now`;
  if (mins < 60) return `${mins}m ${suffix}`;
  if (hrs < 24) return `${hrs}h ${suffix}`;
  return `${days}d ${suffix}`;
}

function describeSchedule(job: ScheduledJob, tz = 'UTC'): string {
  const tzLabel = tz === 'UTC' ? 'UTC' : TIMEZONES.find(t => t.value === tz)?.label.split('(')[0].trim() ?? tz;
  if (job.scheduleType === 'Once') {
    return job.runAt ? `Once at ${fmtDate(job.runAt, tz)}` : 'Once (unscheduled)';
  }
  if (job.scheduleType === 'Interval') {
    const start = job.intervalStartAt ? ` from ${fmtDate(job.intervalStartAt, tz)}` : '';
    const end = job.intervalEndAt ? ` until ${fmtDate(job.intervalEndAt, tz)}` : '';
    return `Every ${job.intervalMinutes}m${start}${end}`;
  }
  if (job.scheduleType === 'Weekly') {
    const days = (job.weeklyDays || []).map(d => DAYS[d]).join(', ');
    // Convert the UTC time to display time in selected zone
    if (job.weeklyTimeUtc) {
      // Reference "today" so the offset reflects current DST rules, not 1970's winter offset
      const refUtc = `${new Date().toISOString().substring(0, 10)}T${job.weeklyTimeUtc}:00Z`;
      const localTime = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(new Date(refUtc));
      return `Weekly: ${days} at ${localTime} (${tzLabel})`;
    }
    return `Weekly: ${days}`;
  }
  return '—';
}

// ── Create Job Form ────────────────────────────────────────────────────────────

interface CreateJobFormProps {
  vms: VmOption[];
  timezone: string;
  onSave: (req: any) => Promise<void>;
  onCancel: () => void;
}

const CreateJobForm: React.FC<CreateJobFormProps> = ({ vms, timezone, onSave, onCancel }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [vmIds, setVmIds] = useState<string[]>([]);
  const [action, setAction] = useState('Stop');
  const [scheduleType, setScheduleType] = useState<'Once' | 'Interval' | 'Weekly'>('Weekly');
  const [runAt, setRunAt] = useState('');
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [intervalStartAt, setIntervalStartAt] = useState('');
  const [intervalEndAt, setIntervalEndAt] = useState('');
  const [weeklyDays, setWeeklyDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [weeklyTime, setWeeklyTime] = useState('22:00');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleDay = (d: number) => {
    setWeeklyDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
  };

  const toggleVm = (id: string) => {
    setVmIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    if (vmIds.length === 0) { setError('Select at least one VM.'); return; }
    if (scheduleType === 'Weekly' && weeklyDays.length === 0) { setError('Select at least one day.'); return; }
    if (scheduleType === 'Interval' && (!intervalMinutes || intervalMinutes < 1)) { setError('Interval must be at least 1 minute.'); return; }

    setSaving(true);
    setError('');
    try {
      // Convert all wall-clock times from the selected timezone to UTC before saving
      const toUtc = (naiveDt: string) => naiveDt ? naiveDtToUtcIso(naiveDt, timezone) : null;

      // For weekly time, derive the UTC time string from the selected timezone offset
      let weeklyTimeUtc = weeklyTime;
      if (scheduleType === 'Weekly' && weeklyTime) {
        // Reference "today" so the offset reflects current DST rules, not 1970's winter offset
        const ref = naiveDtToUtcIso(`${currentDateIn(timezone)}T${weeklyTime}`, timezone);
        weeklyTimeUtc = new Date(ref).toISOString().substring(11, 16);
      }

      await onSave({
        name: name.trim(),
        description: description.trim(),
        vmIds,
        action,
        scheduleType,
        runAt: scheduleType === 'Once' ? toUtc(runAt) : null,
        intervalMinutes: scheduleType === 'Interval' ? intervalMinutes : null,
        intervalStartAt: scheduleType === 'Interval' && intervalStartAt ? toUtc(intervalStartAt) : null,
        intervalEndAt: scheduleType === 'Interval' && intervalEndAt ? toUtc(intervalEndAt) : null,
        weeklyDays: scheduleType === 'Weekly' ? weeklyDays : null,
        weeklyTimeUtc: scheduleType === 'Weekly' ? weeklyTimeUtc : null,
      });
    } catch (e: any) {
      setError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, padding: '1.5rem', marginBottom: '1.5rem' }}>
      <h3 style={{ margin: '0 0 1rem' }}>New Scheduled Job</h3>

      {error && <div style={{ background: '#dc354522', border: '1px solid #dc3545', borderRadius: 6, padding: '0.75rem', marginBottom: '1rem', color: '#ff8f9a' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Job Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Nightly shutdown" style={{ width: '100%', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Description</label>
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" style={{ width: '100%', boxSizing: 'border-box' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Power Action *</label>
          <select value={action} onChange={e => setAction(e.target.value)} style={{ width: '100%' }}>
            {ACTIONS.map(a => <option key={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Schedule Type *</label>
          <select value={scheduleType} onChange={e => setScheduleType(e.target.value as any)} style={{ width: '100%' }}>
            <option value="Once">Once</option>
            <option value="Interval">Repeating Interval</option>
            <option value="Weekly">Weekly (specific days)</option>
          </select>
        </div>
      </div>

      {/* Schedule-specific inputs */}
      {scheduleType === 'Once' && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Run At ({timezone})</label>
          <input type="datetime-local" value={runAt} onChange={e => setRunAt(e.target.value)} />
        </div>
      )}

      {scheduleType === 'Interval' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Interval (minutes) *</label>
            <input type="number" min={1} max={525600} value={intervalMinutes} onChange={e => setIntervalMinutes(Math.max(1, Number(e.target.value)))} style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Start At ({timezone}, optional)</label>
            <input type="datetime-local" value={intervalStartAt} onChange={e => setIntervalStartAt(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>End At ({timezone}, optional)</label>
            <input type="datetime-local" value={intervalEndAt} onChange={e => setIntervalEndAt(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
        </div>
      )}

      {scheduleType === 'Weekly' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Days *</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {DAYS.map((d, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleDay(i)}
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: weeklyDays.includes(i) ? '#007bff' : '#555',
                    background: weeklyDays.includes(i) ? '#007bff33' : 'transparent',
                    color: weeklyDays.includes(i) ? '#4da6ff' : '#b0b0b0',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >{d}</button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Time ({timezone}) *</label>
            <input type="time" value={weeklyTime} onChange={e => setWeeklyTime(e.target.value)} />
          </div>
        </div>
      )}

      {/* VM selection */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: 6, color: '#b0b0b0', fontSize: '0.85rem' }}>Target VMs * ({vmIds.length} selected)</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem', maxHeight: '180px', overflowY: 'auto', background: '#1a1a1e', padding: '0.75rem', borderRadius: 6, border: '1px solid #444' }}>
          {vms.map(vm => (
            <label key={vm.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: vmIds.includes(vm.id) ? '#f0f0f0' : '#b0b0b0', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={vmIds.includes(vm.id)} onChange={() => toggleVm(vm.id)} />
              <span>{vm.name}</span>
              <span style={{ fontSize: '0.75rem', color: vm.powerState === 'Running' ? '#28a745' : '#888', marginLeft: 'auto' }}>{vm.powerState}</span>
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Create Job'}</button>
        <button className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

// ── Job Row ───────────────────────────────────────────────────────────────────

interface JobRowProps {
  canWrite: boolean;
  job: ScheduledJob;
  timezone: string;
  onRunNow: (id: string) => void;
  onToggle: (id: string, status: string) => void;
  onDelete: (id: string) => void;
}

const JobRow: React.FC<JobRowProps> = ({ canWrite, job, timezone, onRunNow, onToggle, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <td>
          <div style={{ fontWeight: 600 }}>{job.name}</div>
          {job.description && <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 2 }}>{job.description}</div>}
        </td>
        <td>
          <span style={{ background: ACTION_COLOR[job.action] + '22', color: ACTION_COLOR[job.action], border: `1px solid ${ACTION_COLOR[job.action]}66`, borderRadius: 4, padding: '0.2rem 0.5rem', fontSize: '0.8rem', fontWeight: 600 }}>
            {job.action}
          </span>
        </td>
        <td style={{ fontSize: '0.85rem', color: '#b0b0b0' }}>{describeSchedule(job, timezone)}</td>
        <td>
          <div style={{ fontSize: '0.85rem' }}>{job.vmNames.join(', ')}</div>
        </td>
        <td style={{ fontSize: '0.8rem', color: '#b0b0b0' }}>
          {job.nextRunAt ? (
            <span title={fmtDate(job.nextRunAt, timezone)}>{fmtRelative(job.nextRunAt)}</span>
          ) : '—'}
        </td>
        <td>
          <span style={{ background: STATUS_COLOR[job.status] + '22', color: STATUS_COLOR[job.status], border: `1px solid ${STATUS_COLOR[job.status]}66`, borderRadius: 12, padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>
            {job.status}
          </span>
        </td>
        <td onClick={e => e.stopPropagation()}>
          {canWrite ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn-small btn-secondary" onClick={() => onRunNow(job.id)} disabled={job.status === 'Completed'} title="Run now">▶</button>
            {job.status === 'Active' ? (
              <button className="btn-small" style={{ background: '#6c757d', color: 'white' }} onClick={() => onToggle(job.id, 'Disabled')} title="Pause">⏸</button>
            ) : job.status === 'Disabled' ? (
              <button className="btn-small btn-primary" onClick={() => onToggle(job.id, 'Active')} title="Resume">▶▶</button>
            ) : null}
            {confirmDelete ? (
              <>
                <button className="btn-small btn-danger" onClick={() => onDelete(job.id)} title="Confirm delete">Confirm</button>
                <button className="btn-small btn-secondary" onClick={() => setConfirmDelete(false)} title="Cancel">✕</button>
              </>
            ) : (
              <button className="btn-small btn-danger" onClick={() => setConfirmDelete(true)} title="Delete">🗑</button>
            )}
          </div>
          ) : (
            <span style={{ color: '#666', fontSize: '0.8rem' }}>—</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ background: '#1e1e23', padding: '0.75rem 1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: 4 }}>Details</div>
                <div style={{ fontSize: '0.85rem', color: '#b0b0b0', lineHeight: 1.8 }}>
                  <div>Created: {fmtDate(job.createdAt, timezone)}</div>
                  <div>Last run: {fmtDate(job.lastRunAt, timezone)}</div>
                  <div>Run count: {job.runCount}</div>
                  <div>VM IDs: {job.vmIds.join(', ')}</div>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: 4 }}>Recent Executions</div>
                {job.recentLogs.length === 0 ? (
                  <div style={{ color: '#666', fontSize: '0.85rem' }}>No executions yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {job.recentLogs.slice(0, 5).map((log, i) => (
                      <div key={i} style={{ fontSize: '0.8rem', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <span style={{ color: log.success ? '#28a745' : '#dc3545', flexShrink: 0 }}>{log.success ? '✓' : '✗'}</span>
                        <span style={{ color: '#888' }}>{fmtDate(log.executedAt, timezone)}</span>
                        <span style={{ color: '#ccc' }}>{log.message}</span>
                        {log.errors.length > 0 && <span style={{ color: '#ff8f9a', fontSize: '0.75rem' }}>{log.errors.join('; ')}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const VmScheduler: React.FC = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  // Mirrors the API policy: scheduler mutations need operator or admin.
  const canWrite = user?.role === 'operator' || user?.role === 'admin';
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [vms, setVms] = useState<VmOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [timezone, setTimezone] = useState(() =>
    TIMEZONES.find(t => t.value === Intl.DateTimeFormat().resolvedOptions().timeZone)?.value ?? 'UTC'
  );

  const loadJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/scheduler/jobs`);
      setJobs(res.data);
    } catch (e: any) {
      setError('Failed to load scheduled jobs.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadVms = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/vm?subscriptionId=all`);
      setVms(res.data.map((v: any) => ({ id: v.id, name: v.name, powerState: v.powerState })));
    } catch {
      // VMs are optional for the form — silently ignore
    }
  }, []);

  useEffect(() => {
    loadJobs();
    loadVms();
    const interval = setInterval(loadJobs, 30000);
    return () => clearInterval(interval);
  }, [loadJobs, loadVms]);

  const handleCreate = async (req: any) => {
    // Errors propagate to CreateJobForm, which shows them inline and stays open.
    await axios.post(`${API_URL}/scheduler/jobs`, req);
    setShowCreate(false);
    await loadJobs();
  };

  const handleRunNow = async (id: string) => {
    try {
      await axios.post(`${API_URL}/scheduler/jobs/${id}/run`);
      showToast('Job triggered successfully.', 'success');
      await loadJobs();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to run job.'), 'error');
    }
  };

  const handleToggle = async (id: string, newStatus: string) => {
    try {
      await axios.patch(`${API_URL}/scheduler/jobs/${id}/status`, { status: newStatus });
      await loadJobs();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to update job status.'), 'error');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${API_URL}/scheduler/jobs/${id}`);
      showToast('Job deleted.', 'success');
      await loadJobs();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to delete job.'), 'error');
    }
  };

  const filtered = jobs.filter(j => {
    if (filterStatus !== 'all' && j.status !== filterStatus) return false;
    if (filterAction !== 'all' && j.action !== filterAction) return false;
    return true;
  });

  // Summary counts
  const active = jobs.filter(j => j.status === 'Active').length;
  const disabled = jobs.filter(j => j.status === 'Disabled').length;
  const completed = jobs.filter(j => j.status === 'Completed').length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>VM Scheduler</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.9rem' }}>Automate power actions on virtual machines</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label style={{ color: '#888', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Timezone:</label>
          <select
            value={timezone}
            onChange={e => setTimezone(e.target.value)}
            style={{ minWidth: 220, background: '#1a1a1e', color: '#f0f0f0', border: '1px solid #444', borderRadius: 5, padding: '0.35rem 0.6rem', fontSize: '0.85rem' }}
          >
            {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
          </select>
          {canWrite && (
            <button className="btn-primary" onClick={() => setShowCreate(s => !s)}>
              {showCreate ? '✕ Cancel' : '+ New Job'}
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Active', value: active, color: '#28a745' },
          { label: 'Disabled', value: disabled, color: '#6c757d' },
          { label: 'Completed', value: completed, color: '#17a2b8' },
        ].map(c => (
          <div key={c.label} onClick={() => setFilterStatus(filterStatus === c.label ? 'all' : c.label)}
            style={{ background: '#25252a', border: `1px solid ${filterStatus === c.label ? c.color : '#444'}`, borderRadius: 8, padding: '1rem', cursor: 'pointer', transition: 'border-color 0.2s' }}>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: c.color }}>{c.value}</div>
            <div style={{ color: '#888', fontSize: '0.85rem', marginTop: 2 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {error && <div style={{ background: '#dc354522', border: '1px solid #dc3545', borderRadius: 6, padding: '0.75rem', marginBottom: '1rem', color: '#ff8f9a' }}>{error}</div>}

      {showCreate && (
        <CreateJobForm vms={vms} timezone={timezone} onSave={handleCreate} onCancel={() => setShowCreate(false)} />
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 130 }}>
          <option value="all">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Disabled">Disabled</option>
          <option value="Completed">Completed</option>
          <option value="Failed">Failed</option>
        </select>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={{ minWidth: 130 }}>
          <option value="all">All Actions</option>
          {ACTIONS.map(a => <option key={a}>{a}</option>)}
        </select>
        <button onClick={loadJobs} style={{ marginLeft: 'auto' }}>🔄 Refresh</button>
        <span style={{ color: '#888', fontSize: '0.85rem' }}>Auto-refresh every 30s</span>
      </div>

      {/* Jobs table */}
      <div style={{ background: '#25252a', borderRadius: 8, overflow: 'hidden', border: '1px solid #444' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#333338' }}>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '0.85rem' }}>Job</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '0.85rem' }}>Action</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '0.85rem' }}>Schedule</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '0.85rem' }}>VMs</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '0.85rem' }}>Next Run</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '0.85rem' }}>Status</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '0.85rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                {jobs.length === 0 ? 'No scheduled jobs yet. Create one to get started.' : 'No jobs match the current filters.'}
              </td></tr>
            ) : (
              filtered.map(job => (
                <JobRow
                  key={job.id}
                  canWrite={canWrite}
                  job={job}
                  timezone={timezone}
                  onRunNow={handleRunNow}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '0.75rem', color: '#555', fontSize: '0.8rem' }}>
        Click a job row to see execution history and details.
      </div>
    </div>
  );
};

export default VmScheduler;
